import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import MerchantChannelEditModal from './MerchantChannelEditModal';
import TableToggle from './TableToggle';
import { formatAmountRange, formatSupportedMerchants } from './merchantChannelConstants';

const TABLE_COLUMNS = [
  'ID',
  '通道名稱',
  '商戶接口',
  '通道類型',
  '幣種類型',
  '排序',
  '可提現',
  '提現區間',
  '提現手續費(%)',
  '可充值',
  '充值區間',
  '充值返佣(%)',
  '操作',
];

export default function OnlinePaymentChannelPage() {
  const [merchants, setMerchants] = useState([]);
  const [coinPayTypes, setCoinPayTypes] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [filterMerchantId, setFilterMerchantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  const records = useMemo(() => {
    if (!filterMerchantId) {
      return allRecords;
    }
    const merchantId = Number(filterMerchantId);
    return allRecords.filter((row) =>
      (row.merchantIds || []).some((id) => Number(id) === merchantId)
    );
  }, [allRecords, filterMerchantId]);

  async function loadMerchants() {
    const data = await adminApi.listMerchants();
    return Array.isArray(data) ? data : [];
  }

  async function loadCoinPayTypes() {
    const data = await adminApi.coinPayTypePage({ pageNo: 1, pageSize: 200 });
    setCoinPayTypes(data?.records || []);
  }

  async function loadChannels() {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listMerchantChannels();
      setAllRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || '載入失敗');
      setAllRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function init() {
      setError('');
      try {
        const merchantList = await loadMerchants();
        setMerchants(merchantList);
        await loadCoinPayTypes();
        await loadChannels();
      } catch (err) {
        setError(err.message || '載入失敗');
        setLoading(false);
      }
    }
    init();
  }, []);

  function openCreate() {
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(payload) {
    setSubmitting(true);
    setError('');
    try {
      if (payload.id) {
        await adminApi.updateMerchantChannel(payload);
        setMessage('已更新');
      } else {
        await adminApi.createMerchantChannel(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      await loadChannels();
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggle(row, field, nextValue, label) {
    if (!row?.id) {
      return;
    }
    const toggleKey = `${row.id}-${field}`;
    setTogglingKey(toggleKey);
    setError('');
    try {
      await adminApi.updateMerchantChannel({ id: row.id, [field]: nextValue });
      setAllRecords((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, [field]: nextValue } : item))
      );
    } catch (err) {
      setError(err.message || `更新${label}失敗`);
    } finally {
      setTogglingKey(null);
    }
  }

  function formatChannelType(row) {
    if (row.type) {
      return row.type;
    }
    if (row.coinPayTypeName) {
      return row.coinPayTypeName;
    }
    return '—';
  }

  function formatFee(value) {
    return value != null ? value : '0';
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>線上支付通道</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          新增通道
        </button>
      </div>

      <form
        className="filter-bar filter-bar-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          setMessage('');
        }}
      >
        <div className="form-field">
          <label htmlFor="channelMerchantSelect">篩選商戶</label>
          <select
            id="channelMerchantSelect"
            value={filterMerchantId}
            onChange={(e) => setFilterMerchantId(e.target.value)}
          >
            <option value="">全部</option>
            {merchants.map((merchant) => (
              <option key={merchant.id} value={merchant.id}>
                {merchant.name} ({merchant.merchantCode})
              </option>
            ))}
          </select>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && (
        <p className="page-desc">
          共 {records.length} 筆
          {filterMerchantId && records.length !== allRecords.length
            ? `（全部 ${allRecords.length} 筆）`
            : ''}
        </p>
      )}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td>{formatSupportedMerchants(row)}</td>
                  <td>{formatChannelType(row)}</td>
                  <td>{row.currencyType || '—'}</td>
                  <td>{row.sort ?? 0}</td>
                  <td className="table-toggle-cell">
                    <TableToggle
                      checked={row.isWithdrawal}
                      disabled={togglingKey === `${row.id}-isWithdrawal`}
                      ariaLabel={`${row.name} 可提現`}
                      onChange={(v) => handleToggle(row, 'isWithdrawal', v, '可提現')}
                    />
                  </td>
                  <td>{formatAmountRange(row.withdrawalMin, row.withdrawalMax)}</td>
                  <td>{formatFee(row.withdrawalFee)}</td>
                  <td className="table-toggle-cell">
                    <TableToggle
                      checked={row.isRecharge}
                      disabled={togglingKey === `${row.id}-isRecharge`}
                      ariaLabel={`${row.name} 可充值`}
                      onChange={(v) => handleToggle(row, 'isRecharge', v, '可充值')}
                    />
                  </td>
                  <td>{formatAmountRange(row.rechargeMin, row.rechargeMax)}</td>
                  <td>{formatFee(row.rechargeRebate)}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEdit(row)}
                      >
                        編輯
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <MerchantChannelEditModal
        open={editOpen}
        row={editRow}
        defaultMerchantId={filterMerchantId ? Number(filterMerchantId) : merchants[0]?.id}
        merchants={merchants}
        coinPayTypes={coinPayTypes}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
          }
        }}
        onSubmit={handleSave}
      />
    </div>
  );
}
